<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    labelCols: {
      type: Number,
      required: false,
      default: 4,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.labelCols,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelCols');
    }
  );

  const act_2095089031469157_emit_2laib7jkf_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_fb55icl4c_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_rz6xjw9lk_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCompanyEditor',
      props: {
        target: 'general',
        item: _.cloneDeep(props.item),
      },
    };
  };
  const act_7349226261686150_emit_0rw2qjew8_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_7315092382398562_reloadComponentData_khcmsjcgp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1zsci7rdb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_khcmsjcgp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1zsci7rdb],
    event: 'reload',
  });
  const act_2095089031469157_emit_2laib7jkf = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_2laib7jkf_getActionArgs,
  });
  const inst_evh_2095089031469157_close_fb55icl4c =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_2laib7jkf,
      event: 'close',
      when: inst_evh_2095089031469157_close_fb55icl4c_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_rz6xjw9lk =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_rz6xjw9lk_getActionArgs,
      events: [inst_evh_2095089031469157_close_fb55icl4c],
    });
  const inst_evh_2095089031469157_clickPreventStop_us3q781qb =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_rz6xjw9lk,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_us3q781qb],
      event: 'click.prevent.stop',
    });
  const act_7349226261686150_emit_0rw2qjew8 = new actionTypes.EmitAction({
    actionArgs: act_7349226261686150_emit_0rw2qjew8_getActionArgs,
  });
  const inst_evh_7349226261686150_reload_r4b08qe6o =
    new actionTypes.EventHandlerImpl({
      action: act_7349226261686150_emit_0rw2qjew8,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_7349226261686150_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7349226261686150_reload_r4b08qe6o],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_1zsci7rdb,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_fb55icl4c,
    inst_evh_2095089031469157_clickPreventStop_us3q781qb,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_7349226261686150_reload_r4b08qe6o,
    inst_evh_7349226261686150_reload,
  };

  const evh_7315092382398562_reload_1zsci7rdb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1zsci7rdb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_fb55icl4c = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_fb55icl4c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_us3q781qb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_us3q781qb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload_r4b08qe6o = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload_r4b08qe6o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Company', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Name')"
          :labelCols="labelCols"
          v-if="item.name"
        >
          <ItpBox>
            <ItpText :text="item.name"> </ItpText>
            <ItpBadge
              :text="pgettext('Company', 'Default')"
              variant="info"
              no-box-shadow
              class="ms-3"
              v-if="item.is_default"
            >
            </ItpBadge>
          </ItpBox>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Number')"
          :labelCols="labelCols"
          :content="item.code"
          v-if="item.code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Meter', 'Status')"
          :labelCols="labelCols"
        >
          <UcResourceStatusCard
            :resource="item"
            @reload="evh_7349226261686150_reload($event, {})"
          >
          </UcResourceStatusCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Client relation')"
          :labelCols="labelCols"
          v-if="item.role === 'partner' && item.relation"
        >
          <UcCompanyTypeBadge :value="item.relation"> </UcCompanyTypeBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Email')"
          :labelCols="labelCols"
          :content="item.emails"
          v-if="item.emails"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Tax number')"
          :labelCols="labelCols"
          v-if="item.tax_number"
        >
          <template>
            <div>
              {{ item.tax_number }}
            </div>
            <ItpBox
              :title="pgettext('Company', 'Tax number history')"
              class="mt-1"
              v-if="item.tax_number_history && item.tax_number_history.length"
            >
              <ItpIcon icon="mdi:history"> </ItpIcon>
              <ItpBox alignItems="center" wrap-items>
                <small
                  :key="val"
                  class="ms-2"
                  v-for="val in item.tax_number_history"
                >
                  {{ val }}
                </small>
              </ItpBox>
            </ItpBox>
          </template>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'EU tax number')"
          :labelCols="labelCols"
          :content="item.eu_tax_number"
          v-if="item.eu_tax_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Registration number')"
          :labelCols="labelCols"
          :content="item.registration_number"
          v-if="item.registration_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Notes')"
          :labelCols="labelCols"
          :content="item.notes"
          v-if="item.notes"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Payment period')"
          :labelCols="labelCols"
          v-if="item.payment_period_days"
        >
          {{ item.payment_period_days }}
          {{ pgettext('InputGroupAddon', 'days') }}
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Discount')"
          :labelCols="labelCols"
          v-if="item.discount"
        >
          {{ item.discount }} %
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Contractor')"
          :labelCols="labelCols"
          :content="gettext('Yes')"
          v-if="item.is_contractor"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Sole proprietor')"
          :labelCols="labelCols"
          :content="gettext('Yes')"
          v-if="item.sole_proprietor"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Natural person')"
          :labelCols="labelCols"
          :content="gettext('Yes')"
          v-if="item.is_natural_person"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Exclude from claims')"
          :labelCols="labelCols"
          :content="gettext('Yes')"
          v-if="item.excluded_from_claims"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'VAT exempt')"
          :labelCols="labelCols"
          :content="gettext('Yes')"
          v-if="item.vat_exempt"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'Groups')"
          :labelCols="labelCols"
          :content="item.groups"
          v-if="item.groups"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Company', 'External key')"
          :labelCols="labelCols"
          :content="item.external_key"
          v-if="item.external_key"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
